import { Component } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { Router } from '@angular/router';
import { Dashbaord } from './dashbaord';
import { OpportunityService } from './opportunity.service';
import { MainWrapperComponent } from '@core/component';
import { AmplitudeService, AuthenticationService } from '@core/services'; // Adjust path as needed
import { UserConfigService, type CrmProvider } from '@core/services';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-dashboard',
  imports: [
    CommonModule,
    DatePipe,
    MainWrapperComponent
  ],
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.scss',
})
export class DashboardComponent {
  oppportunityDetails: Dashbaord[] = [];
  sortField: string = '';
  sortDirection: 'asc' | 'desc' = 'desc';
  sortedOpportunities: any[] = [];
  connectedCrmProvider: CrmProvider | null = null;
  mappedOpportunityIds = new Set<string>();
  isLoading = true;
  showCompanionPrompt = false;

  private static readonly COMPANION_PROMPT_KEY = 'convverse_show_companion_prompt';
  private static readonly COMPANION_URL = 'https://companion.convverse.ai';

  constructor(
    private readonly _opportunityService: OpportunityService,
    private readonly _router: Router,
    private readonly _amplitudeService: AmplitudeService,
    private readonly _authState: AuthenticationService,
    private readonly _userConfigService: UserConfigService
  ) { }

  async ngOnInit() {
    this._maybeShowCompanionPrompt();
    this.isLoading = true;
    try {
      const [oppportunityDetails, connectedProvider] = await Promise.all([
        firstValueFrom(this._opportunityService.getOpportunity()),
        this._userConfigService.loadConnectedCrmProvider(),
      ]);

      this.connectedCrmProvider = connectedProvider;
      if (this.connectedCrmProvider) {
        const mappedIds = await firstValueFrom(this._opportunityService.getMappedOpportunityIds());
        this.mappedOpportunityIds = new Set(mappedIds);
      } else {
        this.mappedOpportunityIds.clear();
      }

      this.oppportunityDetails = oppportunityDetails
        .map((item: Dashbaord) => ({
          ...item,
          has_past_meetings: this.hasPastMeetings(item),
          effective_updated_at: this.getEffectiveUpdatedAt(item),
        }));
      this.sortedOpportunities = [...this.oppportunityDetails];
      this.sortBy('updated_at');
    } catch (error) {
      console.error('Failed to load opportunities:', error);
      this.oppportunityDetails = [];
      this.sortedOpportunities = [];
      this.connectedCrmProvider = null;
      this.mappedOpportunityIds.clear();
    } finally {
      this.isLoading = false;
    }
  }

  get shouldShowCrmColumn(): boolean {
    return !!this.connectedCrmProvider;
  }

  isOpportunityMapped(opportunity: Dashbaord): boolean {
    const opportunityId = opportunity?.opportunity_id?.toString().trim();
    if (!opportunityId) {
      return false;
    }

    return this.mappedOpportunityIds.has(opportunityId);
  }

  openDeatilPage(oppportunityDetails: Dashbaord): void {
    if (!this.hasPastMeetings(oppportunityDetails)) {
      return;
    }

    const id = oppportunityDetails.opportunity_id;
    const opportunity_name = oppportunityDetails.opportunity_name;
    localStorage.setItem('OPPORTUNITY_NAME', opportunity_name);

    // Get user ID from session storage or your user service
    const userInfo = this._authState.getUserDetail();
    let userId = 'anonymous'; // Default fallback

    if (userInfo) {
      userId = (userInfo as any).user_id || userInfo.user?.id || (userInfo as any).id || 'anonymous';
    }

    // Track opportunity selection event with Amplitude
    if (id) {
      this._amplitudeService.trackEvent('opportunity_selection', {
        opportunity_id: id,
        user_id: userId
      });
    }

    if (id) {
      this._router.navigate(['/opportunity-detail', id, '']);
    }
  }

  createQuestion() {
    this._router.navigate(['/add-question']);
  }

  private _maybeShowCompanionPrompt(): void {
    if (localStorage.getItem(DashboardComponent.COMPANION_PROMPT_KEY) === 'true') {
      // One-shot: only surface the prompt on the first dashboard visit after activation.
      localStorage.removeItem(DashboardComponent.COMPANION_PROMPT_KEY);
      this.showCompanionPrompt = true;
      this._amplitudeService.trackEvent('companion_prompt_shown', {});
    }
  }

  openCompanion(): void {
    this._amplitudeService.trackEvent('companion_prompt_cta_clicked', {});
    window.open(DashboardComponent.COMPANION_URL, '_blank', 'noopener,noreferrer');
    this.showCompanionPrompt = false;
  }

  dismissCompanionPrompt(): void {
    this._amplitudeService.trackEvent('companion_prompt_dismissed', {});
    this.showCompanionPrompt = false;
  }

  sortBy(field: string): void {
    if (this.sortField === field) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortField = field;
      this.sortDirection = 'desc';
    }

    this.sortedOpportunities = [...this.oppportunityDetails].sort((a: any, b: any) => {
      if (field === 'updated_at') {
        return this.compareByUpdatedAt(a, b);
      }

      let aValue = a[field];
      let bValue = b[field];

      if (field === 'opportunity_name') {
        const result = String(aValue || '').localeCompare(String(bValue || ''), undefined, {
          sensitivity: 'base'
        });
        return this.sortDirection === 'asc' ? result : -result;
      }

      const result = aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      return this.sortDirection === 'asc' ? result : -result;
    });
  }

  getUpdatedAtValue(opportunity: Dashbaord): string {
    return opportunity.effective_updated_at || opportunity.latest_meeting_created_at || opportunity.updated_at;
  }

  hasPastMeetings(opportunity: Dashbaord): boolean {
    const hasLatestField = Object.prototype.hasOwnProperty.call(opportunity, 'latest_meeting_created_at');
    if (!hasLatestField) {
      // Backward compatibility for old endpoint shape.
      return true;
    }
    return !!opportunity.latest_meeting_created_at;
  }

  private getEffectiveUpdatedAt(opportunity: Dashbaord): string {
    return this.hasPastMeetings(opportunity)
      ? (opportunity.latest_meeting_created_at || opportunity.updated_at)
      : '';
  }

  private getUpdatedAtTimestamp(opportunity: Dashbaord): number {
    const value = this.getUpdatedAtValue(opportunity);
    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? 0 : parsed;
  }

  private compareByUpdatedAt(a: Dashbaord, b: Dashbaord): number {
    const aHasMeetings = this.hasPastMeetings(a);
    const bHasMeetings = this.hasPastMeetings(b);

    // Always keep opportunities with no past meetings at the end.
    if (aHasMeetings && !bHasMeetings) return -1;
    if (!aHasMeetings && bHasMeetings) return 1;

    // For opportunities without meetings, sort alphabetically.
    if (!aHasMeetings && !bHasMeetings) {
      return String(a.opportunity_name || '').localeCompare(String(b.opportunity_name || ''), undefined, {
        sensitivity: 'base'
      });
    }

    const aTime = this.getUpdatedAtTimestamp(a);
    const bTime = this.getUpdatedAtTimestamp(b);
    if (aTime !== bTime) {
      return this.sortDirection === 'asc' ? aTime - bTime : bTime - aTime;
    }

    return String(a.opportunity_name || '').localeCompare(String(b.opportunity_name || ''), undefined, {
      sensitivity: 'base'
    });
  }
}